<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import FormSection from '@/Components/FormSection.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import SecondaryButton from '@/Components/SecondaryButton.vue'
import InputLabel from '@/Components/InputLabel.vue'
import TextInput from '@/Components/TextInput.vue'
import SelectInput from '@/Components/SelectInput.vue'
import { useForm } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import SearchSelect from "@/Components/SearchSelect.vue";
import DialogModal from '@/Components/DialogModal.vue'
import { computed, ref, watch } from "vue";
import { useTenantButtonVisibility, useTenantTextRules } from '@/helpers'

const props = defineProps({
  mode: String,
  style: Object,
  garments: Array,
  scales: Array,
  fabrics: Array,
  clients: Array,
  materialCodes: Array,
  selectedMaterialCodeIds: Array,
  colors: Array,
  multiPhotoColorMode: {
    type: Boolean,
    default: false,
  },
  colorGallery: {
    type: Object,
    default: null,
  },
  mainSizeLoadLimits: {
    type: Array,
    default: () => [],
  },
  returnTo: {
    type: String,
    default: '',
  },
})

const { resolveTextRule } = useTenantTextRules();
const { isButtonVisible, isButtonEnabled } = useTenantButtonVisibility();

const styleEntityLabel = computed(() =>
  resolveTextRule('inventory.style.entity', 'Style', 'inventory.style.entity')
);
const styleNumberLabel = computed(() =>
  resolveTextRule('style_number.label', 'Style Number', 'inventory.style_edit.style_number')
);
const sizeScaleLabel = computed(() =>
  resolveTextRule('size_scale.label', 'Size Scale', 'inventory.style_edit.size_scale')
);
const garmentLabel = computed(() =>
  resolveTextRule(
    'garment.label',
    resolveTextRule('inventory.style_edit.garment_label', 'Garment', 'inventory.style_edit.garment_label'),
    'inventory.style_edit.garment_label'
  )
);
const fabricLabel = computed(() =>
  resolveTextRule('fabric.label', 'Fabric', 'inventory.style_edit.fabric')
);
const editTitle = computed(() =>
  props.style?.styleId
    ? resolveTextRule('inventory.style_edit.edit_title', `Edit ${styleEntityLabel.value}`, 'inventory.style_edit.edit_title')
    : resolveTextRule('inventory.style_edit.create_title', `Create ${styleEntityLabel.value}`, 'inventory.style_edit.create_title')
);
const saveLabel = computed(() =>
  resolveTextRule('inventory.style_edit.save', `Save ${styleEntityLabel.value}`, 'inventory.style_edit.save')
);
const selectSizeScaleLabel = computed(() =>
  resolveTextRule(
    'inventory.style_edit.select_size_scale',
    `Select a ${sizeScaleLabel.value}`,
    'inventory.style_edit.select_size_scale'
  )
);
const selectGarmentLabel = computed(() =>
  resolveTextRule(
    'inventory.style_edit.select_garment',
    `Select a ${garmentLabel.value}`,
    'inventory.style_edit.select_garment'
  )
);
const selectFabricLabel = computed(() =>
  resolveTextRule(
    'inventory.style_edit.select_fabric',
    `Select a ${fabricLabel.value}`,
    'inventory.style_edit.select_fabric'
  )
);
const selectClientLabel = computed(() =>
  resolveTextRule(
    'inventory.style_edit.select_client',
    'Select a client',
    'inventory.style_edit.select_client'
  )
);

const colorsLabel = computed(() =>
  resolveTextRule('inventory.style_edit.colors', 'Colors', 'inventory.style_edit.colors')
);
const colorNameLabel = computed(() =>
  resolveTextRule('inventory.style_edit.color_name', 'Color Name', 'inventory.style_edit.color_name')
);
const colorImageLabel = computed(() =>
  resolveTextRule('inventory.style_edit.color_image', 'Color Image', 'inventory.style_edit.color_image')
);
const removeColorLabel = computed(() =>
  resolveTextRule('inventory.style_edit.remove_color', 'Remove Color', 'inventory.style_edit.remove_color')
);
const removePhotoLabel = computed(() =>
  resolveTextRule('inventory.style_edit.remove_photo', 'Remove Photo', 'inventory.style_edit.remove_photo')
);
const addColorLabel = computed(() =>
  resolveTextRule('inventory.style_edit.add_color', '+ Add Another Color', 'inventory.style_edit.add_color')
);
const notesLabel = computed(() =>
  resolveTextRule('inventory.style_edit.notes', 'Notes', 'inventory.style_edit.notes')
);
const rentalPriceDailyLabel = computed(() =>
  resolveTextRule('inventory.style_edit.rental_price_daily_label', 'Daily', 'inventory.style_edit.rental_price_daily_label')
);
const rentalPriceWeeklyLabel = computed(() =>
  resolveTextRule('inventory.style_edit.rental_price_weekly_label', 'Weekly', 'inventory.style_edit.rental_price_weekly_label')
);
const rentalPriceMonthlyLabel = computed(() =>
  resolveTextRule('inventory.style_edit.rental_price_monthly_label', 'Monthly', 'inventory.style_edit.rental_price_monthly_label')
);
const pricingSectionLabel = computed(() =>
  resolveTextRule('inventory.style_edit.pricing_section', 'Pricing', 'inventory.style_edit.pricing_section')
);
const extraField1Label = computed(() =>
  resolveTextRule('inventory.style_edit.extra_field_1_label', 'Extra Field 1', 'inventory.style_edit.extra_field_1_label')
);
const extraField2Label = computed(() =>
  resolveTextRule('inventory.style_edit.extra_field_2_label', 'Extra Field 2', 'inventory.style_edit.extra_field_2_label')
);
const extraField3Label = computed(() =>
  resolveTextRule('inventory.style_edit.extra_field_3_label', 'Extra Field 3', 'inventory.style_edit.extra_field_3_label')
);
const isGenderVisible = computed(() => isButtonVisible('inventory.style_edit.gender'));
const isClientVisible = computed(() => isButtonVisible('inventory.style_edit.client'));
const isExtraField1Visible = computed(() => isButtonEnabled('inventory.style_edit.extra_field_1', false));
const isExtraField2Visible = computed(() => isButtonEnabled('inventory.style_edit.extra_field_2', false));
const isExtraField3Visible = computed(() => isButtonEnabled('inventory.style_edit.extra_field_3', false));
const isLoadLimitsVisible = computed(() => isButtonVisible('inventory.style_edit.load_limits'));

const initialScaleId = props.style?.scaleNameId ? String(props.style.scaleNameId) : ''

const normalizedColors = (props.colors && props.colors.length
  ? props.colors
  : [{ name: '', image: null }]
).map(c => ({
  colorId: c.colorId ?? c.id ?? null,
  name: c.name ?? '',
  image: c.image ?? null,
  preview: null,
}))

function normalizedColorImages() {
  const images = props.colorGallery?.images ?? []
  if (images.length) {
    return images.map((img) => ({
      imageId: img.imageId ?? null,
      image: img.image ?? null,
      preview: null,
    }))
  }

  return [{ imageId: null, image: null, preview: null }]
}

function buildLoadLimitsFromScale(scaleId) {
  if (!scaleId) return []
  const scale = (props.scales || []).find(s => String(s.id) === String(scaleId))
  if (!scale?.mainSizes?.length) return []
  return scale.mainSizes.map(ms => ({
    sizeScaleId: ms.sizeScaleId,
    label: ms.label,
    load_limit: 0,
  }))
}

function initialLoadLimits() {
  if (props.mainSizeLoadLimits?.length) {
    return props.mainSizeLoadLimits.map(row => ({
      sizeScaleId: row.sizeScaleId,
      label: row.label,
      load_limit: row.load_limit ?? 0,
    }))
  }
  return buildLoadLimitsFromScale(initialScaleId)
}

const form = useForm({
  styleNumber: props.style?.styleNumber || '',
  scaleNameId: props.style?.scaleNameId || '',
  garmentId: props.style?.garmentId || '',
  fabricId: props.style?.fabricId || '',
  gross_gvwr: props.style?.fabric?.fabName || '',
  sex: props.style?.sex || '',
  clientId: props.style?.clientId != null && props.style?.clientId !== ''
    ? Number(props.style.clientId)
    : '',
  notes: props.style?.notes || '',
  extra_field_1: props.style?.extra_field_1 || '',
  extra_field_2: props.style?.extra_field_2 || '',
  extra_field_3_material_codes: (props.selectedMaterialCodeIds || []).map((id) => Number(id)),
  load_limits: initialLoadLimits(),
  colors: normalizedColors,
  color_id: props.colorGallery?.colorId ?? null,
  color_name: props.colorGallery?.name ?? '',
  color_images: normalizedColorImages(),
  rental_price_daily: props.style?.rental_price_daily ?? '',
  rental_price_weekly: props.style?.rental_price_weekly ?? '',
  rental_price_monthly: props.style?.rental_price_monthly ?? '',
})

watch(() => props.colorGallery, (gallery) => {
  if (!props.multiPhotoColorMode) {
    return
  }

  form.color_id = gallery?.colorId ?? null
  form.color_name = gallery?.name ?? ''
  form.color_images = normalizedColorImages()
}, { deep: true })

watch(() => form.scaleNameId, (newScaleId, oldScaleId) => {
  if (oldScaleId == null || oldScaleId === '') {
    if (newScaleId && form.load_limits.length === 0) {
      form.load_limits = buildLoadLimitsFromScale(newScaleId)
    }
    return
  }
  if (String(newScaleId) !== String(oldScaleId)) {
    form.load_limits = buildLoadLimitsFromScale(newScaleId)
  }
})

const showLoadLimits = computed(() => form.scaleNameId && form.load_limits.length > 0)

const loadLimitsModalOpen = ref(false)

const configuredLoadLimitsCount = computed(() =>
  (form.load_limits || []).filter((row) => Number(row.load_limit) > 0).length
)

const loadLimitsButtonHint = computed(() => {
  if (!form.scaleNameId) return 'Select a size scale first'
  if (!form.load_limits.length) return 'No main sizes on this scale'
  if (configuredLoadLimitsCount.value === 0) return 'All sizes set to 0 (disabled)'
  return `${configuredLoadLimitsCount.value} size(s) with a limit`
})

const saveForm = () => {
  const formData = new FormData()

  formData.append('styleNumber', form.styleNumber)
  formData.append('scaleNameId', form.scaleNameId)
  formData.append('garmentId', form.garmentId)
  if (props.multiPhotoColorMode) {
    formData.append('gross_gvwr', form.gross_gvwr || '')
  } else {
    formData.append('fabricId', form.fabricId)
  }
  formData.append('sex', form.sex)
  formData.append('clientId', form.clientId)
  formData.append('notes', form.notes)
  formData.append('extra_field_1', form.extra_field_1)
  formData.append('extra_field_2', form.extra_field_2)
  form.extra_field_3_material_codes.forEach((id, index) => {
    formData.append(`extra_field_3_material_codes[${index}]`, String(id))
  })

  form.load_limits.forEach((row, index) => {
    formData.append(`load_limits[${index}][sizeScaleId]`, String(row.sizeScaleId))
    const val = row.load_limit === '' || row.load_limit == null ? '0' : String(row.load_limit)
    formData.append(`load_limits[${index}][load_limit]`, val)
  })

  if (props.multiPhotoColorMode) {
    if (form.color_id != null) {
      formData.append('color_id', String(form.color_id))
    }
    formData.append('color_name', form.color_name || '')

    form.color_images.forEach((image, index) => {
      if (image.imageId != null) {
        formData.append(`color_images[${index}][imageId]`, String(image.imageId))
      }

      if (image.image instanceof File) {
        formData.append(`color_images[${index}][image]`, image.image)
      } else if (typeof image.image === 'string' && image.image !== '') {
        const filenameOnly = image.image.split('/').pop()
        formData.append(`color_images[${index}][existingImage]`, filenameOnly)
      }
    })

    formData.append('rental_price_daily', form.rental_price_daily === '' || form.rental_price_daily == null ? '' : String(form.rental_price_daily))
    formData.append('rental_price_weekly', form.rental_price_weekly === '' || form.rental_price_weekly == null ? '' : String(form.rental_price_weekly))
    formData.append('rental_price_monthly', form.rental_price_monthly === '' || form.rental_price_monthly == null ? '' : String(form.rental_price_monthly))
  } else {
    form.colors.forEach((color, index) => {
      if (color.colorId != null) {
        formData.append(`colors[${index}][colorId]`, String(color.colorId))
      }

      formData.append(`colors[${index}][name]`, color.name || '')

      if (color.image instanceof File) {
        formData.append(`colors[${index}][image]`, color.image)
      } else if (typeof color.image === 'string' && color.image !== '') {
        const filenameOnly = color.image.split('/').pop()
        formData.append(`colors[${index}][existingImage]`, filenameOnly)
      }
    })
  }

  const url = props.style?.styleId
    ? route('inventory.style.update', { style: props.style.styleId })
    : route('inventory.style.store')

  if (props.style?.styleId) {
    formData.append('_method', 'PUT')
  }

  if (props.returnTo) {
    formData.append('return_to', props.returnTo)
  }

  Inertia.post(url, formData, {
    preserveScroll: true,
    forceFormData: true,
    onSuccess: () => {
      if (props.style?.styleId) {
        Inertia.reload({ only: ['style', 'mainSizeLoadLimits', 'scales', 'colorGallery', 'colors'] })
      }
    },
  })
}

const addColor = () => {
  form.colors.push({ colorId: null, name: '', image: null, preview: null })
}

const removeColor = (index) => {
  form.colors.splice(index, 1)
}

const addPhoto = () => {
  form.color_images.push({ imageId: null, image: null, preview: null })
}

const removePhoto = (index) => {
  if (form.color_images.length <= 1) {
    form.color_images[index] = { imageId: null, image: null, preview: null }
  } else {
    form.color_images.splice(index, 1)
  }

  const fileInput = document.getElementById(`color-image-${index}`)
  if (fileInput) {
    fileInput.value = ''
  }
}

const handleFileChange = (event, index) => {
  const file = event.target.files[0]
  if (file) {
    form.colors[index].image = file
    form.colors[index].preview = URL.createObjectURL(file)
  }
}

const handlePhotoChange = (event, index) => {
  const file = event.target.files[0]
  if (file) {
    form.color_images = form.color_images.map((img, i) =>
      i === index
        ? { ...img, image: file, preview: URL.createObjectURL(file) }
        : img
    )
  }
}

function goBack() {
  if (props.returnTo) {
    Inertia.visit(props.returnTo, { replace: true })
    return
  }

  window.history.back()
  setTimeout(() => {
    window.location.reload()
  }, 200)
}

const clientOptions = computed(() =>
  (props.clients || [])
    .filter((client) => client?.client && (client?.id != null || client?.ID != null))
    .map((client) => ({
      label: client.client,
      value: Number(client.id ?? client.ID),
    }))
);

const clientModel = computed({
  get: () => (form.clientId != null && form.clientId !== '' ? Number(form.clientId) : null),
  set: (v) => { form.clientId = v == null ? "" : Number(v); },
});

const materialCodeOptions = computed(() =>
  (props.materialCodes || [])
    .filter(item => item?.id != null && item?.material_code)
    .map(item => ({
      value: Number(item.id),
      label: item.material_code,
    }))
);

const materialCodeSelection = ref(null)

const selectedMaterialCodeOptions = computed(() => {
  const selectedIds = new Set((form.extra_field_3_material_codes || []).map((value) => Number(value)))

  return materialCodeOptions.value.filter((option) => selectedIds.has(Number(option.value)))
})

const availableMaterialCodeOptions = computed(() => {
  const selectedIds = new Set((form.extra_field_3_material_codes || []).map((value) => Number(value)))

  return materialCodeOptions.value.filter((option) => !selectedIds.has(Number(option.value)))
})

const addMaterialCode = (value) => {
  const numericId = Number(value)
  if (!numericId) {
    return
  }

  const current = new Set((form.extra_field_3_material_codes || []).map((item) => Number(item)))
  current.add(numericId)
  form.extra_field_3_material_codes = Array.from(current).sort((a, b) => a - b)
  materialCodeSelection.value = null
}

const removeMaterialCode = (value) => {
  const numericId = Number(value)
  form.extra_field_3_material_codes = (form.extra_field_3_material_codes || [])
    .map((item) => Number(item))
    .filter((item) => item !== numericId)
}
</script>

<template>
  <AppLayout :title="editTitle">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        {{ editTitle }}
      </h2>
    </template>

    <div class="max-w-4xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection gridClasses="grid-cols-1" :noGrid="true">
        <template #title>
          <div class="max-w-7xl mx-auto px-6 mt-4">
            <SecondaryButton @click="goBack">
              ← Back
            </SecondaryButton>
          </div>
        </template>

        <template #form>
          <div class="flex flex-col gap-4">
            <!-- Style Number -->
            <div>
              <InputLabel :value="styleNumberLabel" />
              <TextInput v-model="form.styleNumber" class="w-full mt-1" />
            </div>

            <!-- Size Scale -->
            <div>
              <InputLabel :value="sizeScaleLabel" />
              <SelectInput v-model="form.scaleNameId" class="mt-1">
                <option value="">{{ selectSizeScaleLabel }}</option>
                <option v-for="scale in props.scales" :key="scale.id" :value="scale.id">
                  {{ scale.scaleName }}
                </option>
              </SelectInput>
              <p class="text-sm text-gray-500 mt-1">Changing this attribute will make the current inventory and load limits unavailable
              </p>
            </div>

            <!-- Garment -->
            <div>
              <InputLabel :value="garmentLabel" />
              <SelectInput v-model="form.garmentId" class="mt-1">
                <option value="">{{ selectGarmentLabel }}</option>
                <option v-for="garment in props.garments" :key="garment.id" :value="garment.id">
                  {{ garment.garmentName }}
                </option>
              </SelectInput>
            </div>

            <!-- Fabric / Gross GVWR -->
            <div v-if="multiPhotoColorMode">
              <InputLabel :value="fabricLabel" />
              <TextInput v-model="form.gross_gvwr" class="w-full mt-1" />
            </div>
            <div v-else>
              <InputLabel :value="fabricLabel" />
              <SelectInput v-model="form.fabricId" class="mt-1">
                <option value="">{{ selectFabricLabel }}</option>
                <option v-for="fabric in props.fabrics" :key="fabric.id" :value="fabric.id">
                  {{ fabric.fabName }}
                </option>
              </SelectInput>
            </div>

            <!-- Client -->
            <div v-if="isClientVisible">
              <InputLabel value="Client" />
              <SearchSelect v-model="clientModel" :options="clientOptions" :placeholder="selectClientLabel" />
            </div>

            <!-- Gender -->
            <div v-if="isGenderVisible">
              <InputLabel value="Gender" />
              <SelectInput v-model="form.sex" class="mt-1">
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="unisex">Unisex</option>
              </SelectInput>
            </div>

            <div v-if="isExtraField1Visible">
              <InputLabel :value="extraField1Label" />
              <TextInput v-model="form.extra_field_1" class="w-full mt-1" />
            </div>

            <div v-if="isExtraField2Visible">
              <InputLabel :value="extraField2Label" />
              <TextInput v-model="form.extra_field_2" class="w-full mt-1" />
            </div>

            <div v-if="isExtraField3Visible">
              <InputLabel :value="extraField3Label" />
              <div class="mt-1 space-y-3">
                <SearchSelect
                  v-model="materialCodeSelection"
                  :options="availableMaterialCodeOptions"
                  placeholder="Search options"
                  filterMode="includes"
                  :selectOnExactMatch="false"
                  @change="addMaterialCode"
                />
                <div class="min-h-[44px] border border-gray-300 rounded-md p-2 flex flex-wrap gap-2">
                  <button
                    v-for="option in selectedMaterialCodeOptions"
                    :key="option.value"
                    type="button"
                    class="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
                    @click="removeMaterialCode(option.value)"
                  >
                    <span>{{ option.label }}</span>
                    <span class="text-base leading-none">&times;</span>
                  </button>
                  <p v-if="materialCodeOptions.length === 0" class="text-sm text-gray-500">
                    No options available.
                  </p>
                  <p v-else-if="selectedMaterialCodeOptions.length === 0" class="text-sm text-gray-500">
                    No options selected.
                  </p>
                </div>
              </div>
            </div>

            <!-- Colors / Photos -->
            <div v-if="multiPhotoColorMode">
              <InputLabel :value="colorsLabel" />
              <div class="border border-gray-300 rounded p-4 mb-4 mt-1 space-y-4">
                <div>
                  <InputLabel for="color-name-gallery" :value="colorNameLabel" />
                  <TextInput id="color-name-gallery" v-model="form.color_name" class="mt-1 w-full" />
                </div>

                <div
                  v-for="(image, index) in form.color_images"
                  :key="index"
                  class="border border-gray-200 rounded p-3 space-y-2"
                >
                  <InputLabel :for="'color-image-' + index" :value="colorImageLabel" />
                  <input
                    :id="'color-image-' + index"
                    type="file"
                    accept="image/*"
                    @change="e => handlePhotoChange(e, index)"
                    class="mt-1 w-full border border-gray-300 rounded-md shadow-sm"
                  />

                  <div v-if="typeof image.image === 'string' && image.image">
                    <img :src="image.image" alt="Photo" class="h-16 mt-2 border border-gray-300 rounded" />
                  </div>

                  <div v-if="image.preview">
                    <img :src="image.preview" alt="Preview" class="h-16 mt-2 border border-gray-300 rounded" />
                  </div>

                  <SecondaryButton
                    type="button"
                    class="mt-2"
                    @click="removePhoto(index)"
                  >
                    {{ removePhotoLabel }}
                  </SecondaryButton>
                </div>
              </div>

              <SecondaryButton type="button" @click="addPhoto">
                {{ addColorLabel }}
              </SecondaryButton>
            </div>

            <div v-else>
              <InputLabel :value="colorsLabel" />
              <div v-for="(color, index) in form.colors" :key="index"
                class="border border-gray-300 rounded p-4 mb-4 mt-1 space-y-2">
                <div>
                  <InputLabel :for="'color-name-' + index" :value="colorNameLabel" />
                  <TextInput :id="'color-name-' + index" v-model="color.name" class="mt-1 w-full" />
                </div>
                <div>
                  <InputLabel :for="'color-image-' + index" :value="colorImageLabel" />
                  <input type="file" accept="image/*" @change="e => handleFileChange(e, index)"
                    class="mt-1 w-full border border-gray-300 rounded-md shadow-sm" />

                  <div v-if="typeof color.image === 'string'">
                    <img :src="color.image" alt="Color image" class="h-16 mt-2 border border-gray-300 rounded" />
                  </div>

                  <div v-if="color.preview">
                    <img :src="color.preview" alt="Preview" class="h-16 mt-2 border border-gray-300 rounded" />
                  </div>
                </div>
                <SecondaryButton
                  v-if="isButtonVisible('inventory.style_edit.remove_color')"
                  type="button"
                  class="mt-2"
                  @click="removeColor(index)"
                >
                  {{ removeColorLabel }}
                </SecondaryButton>
              </div>

              <SecondaryButton
                type="button"
                @click="addColor"
              >
                {{ addColorLabel }}
              </SecondaryButton>
            </div>


            <!-- Inventory load limits (modal) -->
            <div v-if="isLoadLimitsVisible">
              <InputLabel value="Inventory load limit" />
              <div class="mt-2 flex flex-wrap items-center gap-3">
                <SecondaryButton
                  type="button"
                  :disabled="!showLoadLimits"
                  @click="loadLimitsModalOpen = true"
                >
                  Configure load limits
                </SecondaryButton>
                <span class="text-sm text-gray-500">{{ loadLimitsButtonHint }}</span>
              </div>
            </div>

            <!-- Rental pricing (garment-color tenant only) -->
            <div v-if="multiPhotoColorMode" class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 leading-tight">
                {{ pricingSectionLabel }}
              </h3>
              <div class="space-y-4">
                <div>
                  <InputLabel for="rental-price-daily" :value="rentalPriceDailyLabel" />
                  <TextInput
                    id="rental-price-daily"
                    v-model="form.rental_price_daily"
                    type="number"
                    min="0"
                    step="0.01"
                    class="mt-1 w-full"
                  />
                </div>
                <div>
                  <InputLabel for="rental-price-weekly" :value="rentalPriceWeeklyLabel" />
                  <TextInput
                    id="rental-price-weekly"
                    v-model="form.rental_price_weekly"
                    type="number"
                    min="0"
                    step="0.01"
                    class="mt-1 w-full"
                  />
                </div>
                <div>
                  <InputLabel for="rental-price-monthly" :value="rentalPriceMonthlyLabel" />
                  <TextInput
                    id="rental-price-monthly"
                    v-model="form.rental_price_monthly"
                    type="number"
                    min="0"
                    step="0.01"
                    class="mt-1 w-full"
                  />
                </div>
              </div>
            </div>

            <!-- Notes -->
            <div>
              <InputLabel :value="notesLabel" />
              <textarea v-model="form.notes" rows="4"
                class="w-full mt-1 border border-gray-300 rounded-md shadow-sm"></textarea>
            </div>
          </div>
        </template>

        <template #actions>
          <PrimaryButton type="button" @click="saveForm">
            {{ saveLabel }}
          </PrimaryButton>
        </template>
      </FormSection>
    </div>

    <DialogModal
      v-if="isLoadLimitsVisible"
      :show="loadLimitsModalOpen"
      max-width="lg"
      @close="loadLimitsModalOpen = false"
    >
      <template #title>
        Inventory load limits
      </template>

      <template #content>
        <p class="text-sm text-gray-500 mb-4">
          Set a limit per main size for the report color scale and low-stock emails. Use 0 to disable a size.
        </p>
        <div v-if="showLoadLimits" class="space-y-3 max-h-[min(24rem,60vh)] overflow-y-auto pr-1">
          <div
            v-for="(row, index) in form.load_limits"
            :key="row.sizeScaleId ?? index"
            class="flex flex-wrap items-center gap-3"
          >
            <div class="w-24 shrink-0">
              <span class="text-sm font-medium text-gray-700">{{ row.label }}</span>
            </div>
            <TextInput
              v-model.number="row.load_limit"
              type="number"
              min="0"
              class="w-full max-w-xs"
              placeholder="0"
            />
          </div>
        </div>
        <p v-else class="text-sm text-gray-500 italic">
          No main sizes defined for the selected scale.
        </p>
      </template>

      <template #footer>
        <PrimaryButton type="button" @click="loadLimitsModalOpen = false">
          Done
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>
